import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Card, Checkbox, Chip, Divider, FormControlLabel, IconButton, Popover, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography,
} from '@mui/material';
import { ArrowDropDown as CaretIcon } from '@mui/icons-material';
import { EmptyState, TableSkeleton } from './States';
import { HEAD_ROW_HEIGHT, ROW_HEIGHT, TWO_LINE_ROW_HEIGHT } from './tableStyle';

// One table.
//
// There were three: FileTable (rounded card, hover, click-to-select — the
// good one), Users/Companies (`TableContainer component={Paper}`, square
// corners, no hover, an empty state stuffed into a colspan cell), and
// Operations (size="small" with the head styling re-declared inline even
// though the theme already styles MuiTableCell head). Same data shape,
// three different-looking tables.
//
// The theme handles the cell and head styling. This only owns the surface,
// the row behaviour, and the two states a list can be in besides "has rows".
//
// Sorting and filtering live in the header, the way the hiring grid does
// it: a column that gives `sortValue` sorts on a plain click of its name,
// and one that gives `filter` grows a caret that opens the filter — a
// contains box, a low/high pair, or checkboxes with counts, no Apply
// button. Both work on the rows handed in; the page decides what the
// rows are.
//
// The standard every table on the site keeps (ROADMAP "one standard for
// tables"):
// - Headers are one line and stay at the top while the page scrolls; the
//   totals row stays at the bottom.
// - Rows are 48px, or 60px when a row carries a second grey line
//   (`twoLine`). Nothing else.
// - Cells never wrap. Text that does not fit ends in "…" and shows whole on
//   hover. A column that holds long text gives a `width`.

export type FilterKind = 'text' | 'number' | { choices: (row: any) => string; labels?: Record<string, string> };

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  /** Cell content. Kept as a render fn so a column can be a chip or a stack. */
  render: (row: T) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  /** Numbers line up only if they are tabular. */
  numeric?: boolean;
  /**
   * Drop this column on small screens.
   *
   * A table does not shrink; it overflows, and a phone shows whichever
   * columns happen to come first. On /fleet that was Unit and an empty
   * Plate, while Status and Paperwork — the reason anybody opens the fleet
   * on a phone — sat off the right edge behind a scroll nobody discovers.
   *
   * `phone`  — hidden below sm (600px).
   * `tablet` — hidden below md (900px). Implies phone.
   */
  hide?: 'phone' | 'tablet';
  /** What to sort (and, for text/number filters, filter) this column by. Null sorts last. */
  sortValue?: (row: T) => number | string | null | undefined;
  /** Grows a caret in the header that opens the filter. */
  filter?: FilterKind;
}

export interface SortState { key: string; dir: 'asc' | 'desc' }

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  /** Double click, e.g. to open what a click only selects. */
  onRowDoubleClick?: (row: T) => void;
  /** The row shown as selected (by rowKey). */
  selectedKey?: string | null;
  /** Accent stripe down the left of a row that needs attention. */
  rowAccent?: (row: T) => string | undefined;
  empty?: React.ReactNode;
  /** Rows carry a second line (a model under a unit, a role under a name): 60px instead of 48. */
  twoLine?: boolean;
  /** A last row spanning the table — an "add" line, a note. */
  footer?: React.ReactNode;
  /**
   * A totals row under the body, one cell per column by key. Sorting and
   * filtering leave it where it is; it answers for the rows handed in.
   */
  totals?: Partial<Record<string, React.ReactNode>>;
  /** Where sorting starts; the header can change it. */
  defaultSort?: SortState;
}

interface ColumnFilter { text?: string; min?: string; max?: string; choices?: Set<string> }

// Display rather than filtering the array, so a header and its cells can
// never disagree about which columns exist.
function hiddenSx<T>(column: Column<T>) {
  if (column.hide === 'phone') return { display: { xs: 'none', sm: 'table-cell' } };
  if (column.hide === 'tablet') return { display: { xs: 'none', md: 'table-cell' } };
  return {};
}

// The widest a column without a declared width may grow before its text
// ends in "…".
const MAX_CELL_WIDTH = 320;

const ELLIPSIS = {
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  '& .MuiTypography-root': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
} as const;

// A cell that cut its text short says the whole of it on hover.
function titleIfCut(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  if (el.title) return;
  const cut = [el, ...Array.from(el.querySelectorAll<HTMLElement>('.MuiTypography-root'))]
    .some((n) => n.scrollWidth > n.clientWidth + 1);
  if (cut) el.title = el.innerText.replace(/\s*\n\s*/g, ' · ');
}

const isBlank = (v: unknown) => v === null || v === undefined || v === '';

function compare(a: unknown, b: unknown): number {
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return 1;
  if (isBlank(b)) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function filterActive(f?: ColumnFilter): boolean {
  if (!f) return false;
  return !!(f.text && f.text.trim()) || !!(f.min && f.min.trim()) || !!(f.max && f.max.trim()) || !!(f.choices && f.choices.size);
}

function passes<T>(row: T, column: Column<T>, f?: ColumnFilter): boolean {
  if (!f || !column.filter) return true;
  if (column.filter === 'text') {
    const q = (f.text || '').trim().toLowerCase();
    return !q || String(column.sortValue?.(row) ?? '').toLowerCase().includes(q);
  }
  if (column.filter === 'number') {
    const v = column.sortValue?.(row);
    const n = isBlank(v) ? null : Number(v);
    const min = f.min && f.min.trim() !== '' ? Number(f.min) : null;
    const max = f.max && f.max.trim() !== '' ? Number(f.max) : null;
    if (min === null && max === null) return true;
    if (n === null || Number.isNaN(n)) return false;
    if (min !== null && n < min) return false;
    if (max !== null && n > max) return false;
    return true;
  }
  if (!f.choices || !f.choices.size) return true;
  return f.choices.has(column.filter.choices(row));
}

function DataTable<T>({
  columns, rows, rowKey, loading, onRowClick, onRowDoubleClick, selectedKey, rowAccent, empty, twoLine, footer, totals, defaultSort,
}: Props<T>) {
  // The header sticks to the page's own scroll, which only works while
  // nothing between them scrolls. So the table scrolls sideways only when
  // it is actually wider than its card — then it scrolls rather than
  // cutting the last columns off, and gives up the sticky header.
  const boxRef = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(false);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const check = () => {
      const table = box.firstElementChild as HTMLElement | null;
      setWide(!!table && table.scrollWidth > box.clientWidth + 1);
    };
    const observer = new ResizeObserver(check);
    observer.observe(box);
    if (box.firstElementChild) observer.observe(box.firstElementChild);
    check();
    return () => observer.disconnect();
  });

  const [sort, setSort] = useState<SortState | null>(defaultSort || null);
  const [filters, setFilters] = useState<Record<string, ColumnFilter>>({});
  const [menu, setMenu] = useState<{ key: string; anchor: HTMLElement } | null>(null);

  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const anyFilter = Object.entries(filters).some(([k, f]) => byKey.has(k) && filterActive(f));

  const shown = useMemo(() => {
    let out = rows;
    if (anyFilter) out = out.filter((r) => columns.every((c) => passes(r, c, filters[c.key])));
    const col = sort ? byKey.get(sort.key) : null;
    if (sort && col?.sortValue) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const sv = col.sortValue;
      out = [...out].sort((a, b) => {
        const va = sv(a);
        const vb = sv(b);
        // Blanks last whichever way the column points.
        if (isBlank(va) || isBlank(vb)) return compare(va, vb);
        return compare(va, vb) * dir;
      });
    }
    return out;
  }, [rows, columns, filters, sort, anyFilter, byKey]);

  // A click on the name cycles: first the natural way (high to low for
  // numbers, A to Z for words), then the other way, then back to the
  // page's own order.
  const toggleSort = (c: Column<T>) => {
    if (!c.sortValue) return;
    const first: SortState['dir'] = c.numeric ? 'desc' : 'asc';
    setSort((s) => {
      if (s?.key !== c.key) return { key: c.key, dir: first };
      if (s.dir === first) return { key: c.key, dir: first === 'desc' ? 'asc' : 'desc' };
      return null;
    });
  };
  const setFilter = (key: string, patch: Partial<ColumnFilter>) => setFilters((f) => ({ ...f, [key]: { ...f[key], ...patch } }));
  const clearFilters = () => setFilters({});

  const menuColumn = menu ? byKey.get(menu.key) : null;

  return (
    // `clip`, not `hidden`: rounds the corners without becoming a scroll
    // box, so the header can stick to the page.
    <Card sx={{ overflow: 'clip' }}>
      {loading ? (
        <TableSkeleton columns={columns.length} />
      ) : rows.length === 0 ? (
        empty ?? <EmptyState title="Nothing here yet" compact />
      ) : (
        <TableContainer ref={boxRef} sx={{ overflowX: wide ? 'auto' : 'visible' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((c) => {
                  const active = filterActive(filters[c.key]);
                  const sorted = sort?.key === c.key;
                  return (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sortDirection={sorted ? sort!.dir : false}
                      // A fixed width is a desktop instruction. Held on a
                      // phone it makes the table wider than the screen and
                      // the last column scrolls out of sight.
                      sx={{
                        height: HEAD_ROW_HEIGHT, py: 0,
                        width: { xs: 'auto', sm: c.width }, ...hiddenSx(c),
                        ...(c.filter && { '& .caret': { opacity: active ? 1 : 0 }, '&:hover .caret': { opacity: 1 } }),
                      }}
                    >
                      {(() => {
                        // The name sits where the cells' text sits: flush
                        // right over numbers, flush left over words. The
                        // arrow and the caret go on the far side of it,
                        // so they never push the name off the numbers.
                        const right = c.align === 'right';
                        const caret = c.filter && (
                          <IconButton
                            className="caret"
                            size="small"
                            aria-label={`Filter ${typeof c.header === 'string' ? c.header : c.key}`}
                            onClick={(e) => setMenu({ key: c.key, anchor: e.currentTarget })}
                            sx={{ p: 0, color: active ? 'primary.main' : 'text.disabled', transition: 'opacity 120ms' }}
                          >
                            <CaretIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        );
                        const name = c.sortValue ? (
                          <TableSortLabel
                            active={sorted}
                            direction={sorted ? sort!.dir : (c.numeric ? 'desc' : 'asc')}
                            onClick={() => toggleSort(c)}
                            // The arrow takes room only while the column is
                            // sorted, so the filter caret sits by the name.
                            sx={{
                              fontWeight: active ? 700 : undefined,
                              ...(right && { flexDirection: 'row-reverse', '& .MuiTableSortLabel-icon': { ml: 0, mr: 0.5 } }),
                              ...(!sorted && { '& .MuiTableSortLabel-icon': { display: 'none' } }),
                            }}
                          >
                            {c.header}
                          </TableSortLabel>
                        ) : <Box component="span" sx={{ fontWeight: active ? 700 : undefined }}>{c.header}</Box>;
                        return (
                          <Stack direction="row" alignItems="center" gap={0.25} sx={{ justifyContent: right ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start' }}>
                            {right ? <>{caret}{name}</> : <>{name}{caret}</>}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((row) => {
                const accent = rowAccent?.(row);
                const key = rowKey(row);
                return (
                  <TableRow
                    key={key}
                    hover={!!onRowClick}
                    selected={selectedKey != null && selectedKey === key}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                    sx={{
                      height: twoLine ? TWO_LINE_ROW_HEIGHT : ROW_HEIGHT,
                      cursor: onRowClick ? 'pointer' : 'default',
                      // A 3px stripe rather than a tinted row: forty tinted
                      // rows is a wall of colour, a stripe still scans.
                      ...(accent && {
                        boxShadow: `inset 3px 0 0 ${accent}`,
                      }),
                    }}
                  >
                    {columns.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        onMouseEnter={titleIfCut}
                        sx={{
                          py: 0,
                          ...ELLIPSIS,
                          ...(c.numeric && { fontVariantNumeric: 'tabular-nums' }),
                          // A cell needs a maximum for "…" to happen; without
                          // one it widens instead — which is how "2018
                          // Chevrolet Express 3500" pushed the last column
                          // off a phone screen.
                          maxWidth: { xs: 150, sm: c.width ?? MAX_CELL_WIDTH },
                          ...hiddenSx(c),
                        }}
                      >
                        {c.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {anyFilter && shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ borderBottom: 0 }}>
                    <EmptyState title="Nothing matches these filters" compact action={{ label: 'Clear filters', onClick: clearFilters }} />
                  </TableCell>
                </TableRow>
              )}
              {totals && (
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{
                        fontWeight: 700, borderTop: '2px solid', borderTopColor: 'divider', borderBottom: 0,
                        // Stays in view at the bottom, as the header does at the top.
                        position: 'sticky', bottom: 0, zIndex: 1, bgcolor: 'background.paper', whiteSpace: 'nowrap',
                        ...(c.numeric && { fontVariantNumeric: 'tabular-nums' }),
                        ...hiddenSx(c),
                      }}
                    >
                      {totals[c.key] ?? null}
                    </TableCell>
                  ))}
                </TableRow>
              )}
              {footer && (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ borderBottom: 0, py: 1 }}>{footer}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {anyFilter && shown.length > 0 && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">{shown.length} of {rows.length} shown</Typography>
              <Button size="small" onClick={clearFilters} sx={{ py: 0 }}>Clear filters</Button>
            </Stack>
          )}
        </TableContainer>
      )}

      <Popover
        open={!!menu}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {menuColumn && (() => {
          const c = menuColumn;
          const f = filters[c.key] || {};
          const sortRow = c.sortValue && (
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              <Chip size="small" label={c.numeric ? 'High to low' : 'Z to A'} variant={sort?.key === c.key && sort.dir === 'desc' ? 'filled' : 'outlined'} onClick={() => setSort({ key: c.key, dir: 'desc' })} />
              <Chip size="small" label={c.numeric ? 'Low to high' : 'A to Z'} variant={sort?.key === c.key && sort.dir === 'asc' ? 'filled' : 'outlined'} onClick={() => setSort({ key: c.key, dir: 'asc' })} />
              <Chip size="small" label="No sorting" variant={sort?.key === c.key ? 'outlined' : 'filled'} onClick={() => setSort(null)} />
            </Stack>
          );
          let body: React.ReactNode = null;
          if (c.filter === 'text') {
            body = <TextField autoFocus size="small" placeholder="Contains" value={f.text || ''} onChange={(e) => setFilter(c.key, { text: e.target.value })} inputProps={{ 'aria-label': `${typeof c.header === 'string' ? c.header : c.key} contains` }} />;
          } else if (c.filter === 'number') {
            body = (
              <Stack direction="row" gap={1}>
                <TextField autoFocus size="small" placeholder="From" value={f.min || ''} onChange={(e) => setFilter(c.key, { min: e.target.value })} inputProps={{ inputMode: 'decimal', 'aria-label': `${typeof c.header === 'string' ? c.header : c.key} from` }} />
                <TextField size="small" placeholder="To" value={f.max || ''} onChange={(e) => setFilter(c.key, { max: e.target.value })} inputProps={{ inputMode: 'decimal', 'aria-label': `${typeof c.header === 'string' ? c.header : c.key} to` }} />
              </Stack>
            );
          } else if (c.filter) {
            const choiceOf = c.filter.choices;
            const labels = c.filter.labels || {};
            const counts = new Map<string, number>();
            rows.forEach((r) => { const v = choiceOf(r); counts.set(v, (counts.get(v) || 0) + 1); });
            const options = Array.from(counts.keys()).sort((a, b) => (labels[a] || a).localeCompare(labels[b] || b));
            const current = f.choices || new Set<string>();
            const setTo = (next: Set<string>) => setFilter(c.key, { choices: next.size === options.length ? new Set() : next });
            body = (
              <Stack>
                <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Chip label="All" size="small" variant={current.size === 0 ? 'filled' : 'outlined'} onClick={() => setTo(new Set())} />
                </Stack>
                {options.map((o) => (
                  <FormControlLabel
                    key={o}
                    sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13, flex: 1 } }}
                    control={(
                      <Checkbox
                        size="small"
                        sx={{ py: 0.4 }}
                        checked={current.size === 0 || current.has(o)}
                        onChange={() => {
                          if (current.size === 0) { const next = new Set(options); next.delete(o); setTo(next); return; }
                          const next = new Set(current);
                          if (next.has(o)) next.delete(o); else next.add(o);
                          setTo(next);
                        }}
                      />
                    )}
                    label={(
                      <Stack direction="row" component="span" sx={{ width: '100%' }}>
                        <Box component="span" sx={{ flex: 1 }}>{labels[o] || o || '—'}</Box>
                        <Box component="span" sx={{ color: 'text.disabled' }}>{counts.get(o)}</Box>
                      </Stack>
                    )}
                  />
                ))}
              </Stack>
            );
          }
          return (
            <Stack sx={{ p: 1.5, width: 240 }} gap={1}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{c.header}</Typography>
              {sortRow}
              {sortRow && body && <Divider />}
              {body}
              {filterActive(f) && <Button size="small" onClick={() => setFilters((all) => { const out = { ...all }; delete out[c.key]; return out; })} sx={{ alignSelf: 'flex-start', py: 0 }}>Clear</Button>}
            </Stack>
          );
        })()}
      </Popover>
    </Card>
  );
}

export default DataTable;
